import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { submitBatteryA, type SJTResponse, type FCResponse, type SCResponse, type BatteryAProfile } from '@/services/batteryA';
import { useTelegram } from '@/hooks/useTelegram';
import { ARCHETYPE_V2_INFO, type ArchetypeCode } from '@/types';

// ============================================
// QUESTION DATA
// ============================================

interface SJTQuestion {
  id: string;
  situation: string;
  options: { key: string; text: string }[];
}

interface FCQuestion {
  id: string;
  optionA: string;
  optionB: string;
}

interface SCQuestion {
  number: number;
  stem: string;
}

const SJT_QUESTIONS: SJTQuestion[] = [
  {
    id: 'SJT-1',
    situation: 'В общем чате разгорается конфликт. Два участника перешли на личности, атмосфера накаляется. Ты не модератор, но тебя уважают.',
    options: [
      { key: 'A', text: 'Попрошу всех остановиться, предложу разобрать факты спокойно' },
      { key: 'B', text: 'Напишу каждому в личку, постараюсь понять обе стороны' },
      { key: 'C', text: 'Переведу тему шуткой или мемом, чтобы разрядить' },
      { key: 'D', text: 'Прямо скажу, что так общаться неприемлемо' },
    ],
  },
  {
    id: 'SJT-2',
    situation: 'Тебе предложили задачу, которая выше текущего уровня. Это вызов, но и риск провалиться. Времени на раскачку нет.',
    options: [
      { key: 'A', text: 'Возьмусь — рост важнее комфорта' },
      { key: 'B', text: 'Отдохну и проведу время с близкими — сейчас не время рисковать' },
      { key: 'C', text: 'Возьмусь, но сначала составлю чёткий план с дедлайнами' },
      { key: 'D', text: 'Сделаю то, что давно откладывал — это важнее нового вызова' },
    ],
  },
  {
    id: 'SJT-3',
    situation: 'Новый человек в группе чувствует себя не в своей тарелке. Остальные заняты своими делами, никто его не замечает.',
    options: [
      { key: 'A', text: 'Подойду, познакомлюсь, помогу найти кого-то с общими интересами' },
      { key: 'B', text: 'Дам пару практических советов: куда обратиться, что почитать' },
      { key: 'C', text: 'Предложу вместе сделать что-то — так легче влиться' },
      { key: 'D', text: 'Каждый должен сам адаптироваться — это его путь' },
    ],
  },
  {
    id: 'SJT-4',
    situation: 'На мероприятии предлагают странную активность, которую ты никогда не пробовал. Часть людей уже отказалась.',
    options: [
      { key: 'A', text: 'Уточню правила и что конкретно будет происходить' },
      { key: 'B', text: 'Вежливо откажусь — мне это не подходит' },
      { key: 'C', text: 'Попробую — это шанс узнать что-то новое о себе' },
      { key: 'D', text: 'Скажу организаторам, что это не ок — надо предупреждать заранее' },
    ],
  },
  {
    id: 'SJT-5',
    situation: 'Команда решает, куда направить ограниченные ресурсы: на проверенный проект или на рискованную идею с большим потенциалом.',
    options: [
      { key: 'A', text: 'Предложу критерии оценки и измеримые метрики для решения' },
      { key: 'B', text: 'Спрошу, как это скажется на людях — их устойчивость важнее' },
      { key: 'C', text: 'Поддержу рискованную идею — яркий результат стоит того' },
      { key: 'D', text: 'Пусть каждый проголосует — решим коллективно' },
    ],
  },
  {
    id: 'SJT-6',
    situation: 'Ты подвёл человека, который на тебя рассчитывал. Обещание не выполнено, человек расстроен.',
    options: [
      { key: 'A', text: 'Честно признаю ошибку и предложу, как компенсировать' },
      { key: 'B', text: 'Объясню причины — контекст поможет понять ситуацию' },
      { key: 'C', text: 'Постараюсь сгладить ситуацию, чтобы не обострять' },
      { key: 'D', text: 'Разряжу обстановку и переведу в конструктив с юмором' },
    ],
  },
];

const FC_QUESTIONS: FCQuestion[] = [
  {
    id: 'FC-1',
    optionA: 'Мне важнее дарить тепло и поддержку людям вокруг',
    optionB: 'Мне важнее двигать и менять ситуацию вокруг',
  },
  {
    id: 'FC-2',
    optionA: 'Меня вдохновляет неизвестность и свобода',
    optionB: 'Мне спокойнее, когда есть ясные правила и план',
  },
  {
    id: 'FC-3',
    optionA: 'Я замечаю красоту и хочу создавать',
    optionB: 'Я хочу разобраться в причинах и объяснить',
  },
  {
    id: 'FC-4',
    optionA: 'Я скорее соединяю людей между собой',
    optionB: 'Я скорее задаю направление для группы',
  },
  {
    id: 'FC-5',
    optionA: 'Лёгкость и юмор — мой способ справляться',
    optionB: 'Глубина и точность — мой способ справляться',
  },
  {
    id: 'FC-6',
    optionA: 'Я заряжаюсь от вдохновения и символов',
    optionB: 'Я заряжаюсь от дисциплины и навыков',
  },
];

const SC_QUESTIONS: SCQuestion[] = [
  { number: 1, stem: 'Когда я попадаю в новую компанию, я обычно…' },
  { number: 2, stem: 'В отношениях и дружбе для меня самое важное…' },
  { number: 3, stem: 'Когда мне трудно, я чаще всего…' },
  { number: 4, stem: 'В ближайший месяц я хочу стать сильнее в…' },
];

const TOTAL_QUESTIONS = SJT_QUESTIONS.length + FC_QUESTIONS.length + SC_QUESTIONS.length;

// ============================================
// MAIN COMPONENT
// ============================================

type BatteryStep = 'sjt' | 'fc' | 'sc' | 'loading' | 'result';

interface BatteryAProps {
  onComplete: (profile: BatteryAProfile) => void;
  onBack?: () => void;
}

export function BatteryA({ onComplete, onBack }: BatteryAProps) {
  const [step, setStep] = useState<BatteryStep>('sjt');
  const [sjtIndex, setSjtIndex] = useState(0);
  const [fcIndex, setFcIndex] = useState(0);
  const [scIndex, setScIndex] = useState(0);

  const [sjtResponses, setSjtResponses] = useState<SJTResponse[]>([]);
  const [fcResponses, setFcResponses] = useState<FCResponse[]>([]);
  const [scResponses, setScResponses] = useState<SCResponse[]>([]);

  const [profile, setProfile] = useState<BatteryAProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { hapticFeedback } = useTelegram();

  // Calculate progress
  const completedCount =
    sjtResponses.length +
    fcResponses.length +
    scResponses.length;

  const handleSJTAnswer = useCallback((best: string, worst: string) => {
    const q = SJT_QUESTIONS[sjtIndex];
    const newResp: SJTResponse = { question: q.id, best, worst };
    const updated = [...sjtResponses, newResp];
    setSjtResponses(updated);
    hapticFeedback('light');

    if (sjtIndex + 1 < SJT_QUESTIONS.length) {
      setSjtIndex(sjtIndex + 1);
    } else {
      setStep('fc');
    }
  }, [sjtIndex, sjtResponses, hapticFeedback]);

  const handleFCAnswer = useCallback((choice: string) => {
    const q = FC_QUESTIONS[fcIndex];
    const newResp: FCResponse = { question: q.id, choice };
    const updated = [...fcResponses, newResp];
    setFcResponses(updated);
    hapticFeedback('light');

    if (fcIndex + 1 < FC_QUESTIONS.length) {
      setFcIndex(fcIndex + 1);
    } else {
      setStep('sc');
    }
  }, [fcIndex, fcResponses, hapticFeedback]);

  const handleSCAnswer = useCallback((text: string) => {
    const q = SC_QUESTIONS[scIndex];
    const newResp: SCResponse = { question: q.number, text };
    const updated = [...scResponses, newResp];
    setScResponses(updated);
    hapticFeedback('light');

    if (scIndex + 1 < SC_QUESTIONS.length) {
      setScIndex(scIndex + 1);
    } else {
      // All done — submit
      submitAll(sjtResponses, fcResponses, updated);
    }
  }, [scIndex, scResponses, sjtResponses, fcResponses, hapticFeedback]);

  const submitAll = async (
    sjt: SJTResponse[],
    fc: FCResponse[],
    sc: SCResponse[],
  ) => {
    setStep('loading');
    try {
      const result = await submitBatteryA(sjt, fc, sc);
      setProfile(result.profile);
      setStep('result');
      hapticFeedback('success');
    } catch (err) {
      console.error('Battery A submission failed:', err);
      setError('Не удалось обработать результат. Попробуй ещё раз.');
      setStep('sc'); // Go back to last step
      hapticFeedback('error');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-950 p-6">
        <div className="text-center">
          <span className="text-5xl mb-4 block">😔</span>
          <h2 className="text-xl font-semibold text-white mb-2">Что-то пошло не так</h2>
          <p className="text-primary-300 mb-6">{error}</p>
          <Button onClick={() => { setError(null); submitAll(sjtResponses, fcResponses, scResponses); }}>
            Попробовать снова
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-primary-950">
      {/* Header with progress */}
      <header className="flex-shrink-0 border-b border-primary-800/50 bg-primary-900/50 backdrop-blur-sm px-4 pb-3 safe-area-top">
        <div className="flex items-center justify-between mb-2">
          {onBack && step === 'sjt' && sjtIndex === 0 ? (
            <button onClick={onBack} className="text-primary-400 text-sm">
              Назад
            </button>
          ) : (
            <div />
          )}
          <span className="text-sm text-primary-400">
            {step !== 'result' && step !== 'loading' && `${completedCount + 1} из ${TOTAL_QUESTIONS}`}
          </span>
        </div>
        {step !== 'result' && step !== 'loading' && (
          <ProgressBar current={completedCount} total={TOTAL_QUESTIONS} />
        )}
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        <AnimatePresence mode="wait">
          {step === 'sjt' && (
            <SJTScreen
              key={`sjt-${sjtIndex}`}
              question={SJT_QUESTIONS[sjtIndex]}
              onAnswer={handleSJTAnswer}
            />
          )}
          {step === 'fc' && (
            <FCScreen
              key={`fc-${fcIndex}`}
              question={FC_QUESTIONS[fcIndex]}
              onAnswer={handleFCAnswer}
            />
          )}
          {step === 'sc' && (
            <SCScreen
              key={`sc-${scIndex}`}
              question={SC_QUESTIONS[scIndex]}
              onAnswer={handleSCAnswer}
            />
          )}
          {step === 'loading' && (
            <LoadingAnalysis key="loading" />
          )}
          {step === 'result' && profile && (
            <ResultScreen
              key="result"
              profile={profile}
              onContinue={() => onComplete(profile)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============================================
// PROGRESS BAR
// ============================================

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="w-full bg-primary-800 rounded-full h-1.5">
      <motion.div
        className="bg-primary-400 h-1.5 rounded-full"
        initial={false}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.3 }}
      />
    </div>
  );
}

// ============================================
// SJT SCREEN
// ============================================

function SJTScreen({ question, onAnswer }: {
  question: SJTQuestion;
  onAnswer: (best: string, worst: string) => void;
}) {
  const [best, setBest] = useState<string | null>(null);
  const [worst, setWorst] = useState<string | null>(null);

  const canSubmit = best !== null && worst !== null && best !== worst;

  const handleOptionClick = (key: string, mode: 'best' | 'worst') => {
    if (mode === 'best') {
      setBest(key === best ? null : key);
      if (key === worst) setWorst(null);
    } else {
      setWorst(key === worst ? null : key);
      if (key === best) setBest(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.25 }}
      className="flex-1 flex flex-col p-4 safe-area-bottom"
    >
      {/* Section label */}
      <div className="text-xs text-primary-500 uppercase tracking-wide mb-3">Ситуации</div>

      {/* Situation text */}
      <p className="text-white text-base leading-relaxed mb-5">
        {question.situation}
      </p>

      {/* Instructions */}
      <p className="text-primary-400 text-sm mb-4">
        Выбери, что ты <span className="text-green-400">скорее сделаешь</span> и что <span className="text-red-400">скорее не сделаешь</span>
      </p>

      {/* Options */}
      <div className="space-y-2 flex-1">
        {question.options.map((opt) => {
          const isBest = best === opt.key;
          const isWorst = worst === opt.key;

          return (
            <div key={opt.key} className="flex items-center gap-2">
              {/* Best button */}
              <button
                onClick={() => handleOptionClick(opt.key, 'best')}
                className={`w-8 h-8 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs font-bold transition-colors ${
                  isBest
                    ? 'border-green-400 bg-green-400/20 text-green-400'
                    : 'border-primary-600 text-primary-500 hover:border-green-400/50'
                }`}
              >
                {isBest ? '+' : ''}
              </button>

              {/* Option text */}
              <div
                className={`flex-1 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  isBest
                    ? 'border-green-400/50 bg-green-400/10 text-white'
                    : isWorst
                    ? 'border-red-400/50 bg-red-400/10 text-primary-300'
                    : 'border-primary-700 bg-primary-800/50 text-primary-200'
                }`}
              >
                <span className="font-medium text-primary-400 mr-1.5">{opt.key}.</span>
                {opt.text}
              </div>

              {/* Worst button */}
              <button
                onClick={() => handleOptionClick(opt.key, 'worst')}
                className={`w-8 h-8 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs font-bold transition-colors ${
                  isWorst
                    ? 'border-red-400 bg-red-400/20 text-red-400'
                    : 'border-primary-600 text-primary-500 hover:border-red-400/50'
                }`}
              >
                {isWorst ? '−' : ''}
              </button>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-xs text-primary-500 mt-3 mb-3">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full border border-green-400 inline-block" /> скорее так
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full border border-red-400 inline-block" /> скорее нет
        </span>
      </div>

      {/* Submit */}
      <Button
        onClick={() => canSubmit && onAnswer(best!, worst!)}
        disabled={!canSubmit}
        fullWidth
      >
        Далее
      </Button>
    </motion.div>
  );
}

// ============================================
// FC SCREEN
// ============================================

function FCScreen({ question, onAnswer }: {
  question: FCQuestion;
  onAnswer: (choice: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.25 }}
      className="flex-1 flex flex-col p-4 safe-area-bottom"
    >
      <div className="text-xs text-primary-500 uppercase tracking-wide mb-3">Выбор</div>

      <p className="text-primary-300 text-sm mb-6">Что ближе тебе?</p>

      <div className="flex-1 flex flex-col justify-center gap-4">
        <button
          onClick={() => setSelected('A')}
          className={`p-5 rounded-xl border-2 text-left transition-all ${
            selected === 'A'
              ? 'border-primary-400 bg-primary-400/10 text-white'
              : 'border-primary-700 bg-primary-800/30 text-primary-200 hover:border-primary-500'
          }`}
        >
          <span className="text-base leading-relaxed">{question.optionA}</span>
        </button>

        <div className="text-center text-primary-600 text-sm">или</div>

        <button
          onClick={() => setSelected('B')}
          className={`p-5 rounded-xl border-2 text-left transition-all ${
            selected === 'B'
              ? 'border-primary-400 bg-primary-400/10 text-white'
              : 'border-primary-700 bg-primary-800/30 text-primary-200 hover:border-primary-500'
          }`}
        >
          <span className="text-base leading-relaxed">{question.optionB}</span>
        </button>
      </div>

      <div className="mt-4">
        <Button
          onClick={() => selected && onAnswer(selected)}
          disabled={!selected}
          fullWidth
        >
          Далее
        </Button>
      </div>
    </motion.div>
  );
}

// ============================================
// SC SCREEN
// ============================================

function SCScreen({ question, onAnswer }: {
  question: SCQuestion;
  onAnswer: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const maxLen = 200;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.25 }}
      className="flex-1 flex flex-col p-4 safe-area-bottom"
    >
      <div className="text-xs text-primary-500 uppercase tracking-wide mb-3">Завершение</div>

      <p className="text-white text-xl font-medium leading-relaxed mb-6">
        {question.stem}
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, maxLen))}
        placeholder="Одно предложение..."
        rows={3}
        className="w-full bg-primary-800/50 border border-primary-700 rounded-xl p-4 text-white placeholder-primary-500 resize-none focus:outline-none focus:border-primary-400 text-base"
      />

      <div className="flex justify-end mt-2 mb-4">
        <span className={`text-xs ${text.length > maxLen * 0.9 ? 'text-amber-400' : 'text-primary-500'}`}>
          {text.length}/{maxLen}
        </span>
      </div>

      <div className="mt-auto">
        <Button
          onClick={() => text.trim() && onAnswer(text.trim())}
          disabled={!text.trim()}
          fullWidth
        >
          {question.number < SC_QUESTIONS.length ? 'Далее' : 'Завершить'}
        </Button>
      </div>
    </motion.div>
  );
}

// ============================================
// LOADING SCREEN
// ============================================

function LoadingAnalysis() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col items-center justify-center p-6"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-16 h-16 rounded-full border-4 border-primary-700 border-t-primary-400 mb-6"
      />
      <h2 className="text-xl font-semibold text-white mb-2">Анализируем ответы</h2>
      <p className="text-primary-400 text-center">Подожди пару секунд...</p>
    </motion.div>
  );
}

// ============================================
// RESULT SCREEN
// ============================================

function ResultScreen({ profile, onContinue }: {
  profile: BatteryAProfile;
  onContinue: () => void;
}) {
  const primary = ARCHETYPE_V2_INFO[profile.primary as ArchetypeCode];
  const secondary = ARCHETYPE_V2_INFO[profile.secondary as ArchetypeCode];
  const shadow = ARCHETYPE_V2_INFO[profile.shadow as ArchetypeCode];

  if (!primary) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-primary-400">Не удалось загрузить результат</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex-1 flex flex-col p-5 safe-area-bottom overflow-y-auto"
    >
      {/* Header with both archetypes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-5"
      >
        <p className="text-xs text-primary-500 uppercase tracking-wide mb-2">Твой архетип</p>
        <h1 className="text-2xl font-bold text-white">
          {primary.displayName}
          {secondary && <span className="text-primary-400"> / {secondary.displayName}</span>}
        </h1>
      </motion.div>

      {/* Primary archetype block */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-4 p-4 rounded-xl bg-gradient-to-br from-indigo-900/30 to-primary-800/30 border border-indigo-700/30"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{primary.emoji}</span>
          <div>
            <h3 className="font-bold text-white">{primary.displayName}</h3>
            <p className="text-xs text-primary-500">{primary.nameRu}</p>
          </div>
        </div>
        <p className="text-sm text-primary-300 leading-relaxed">{primary.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {primary.strengths.map((s) => (
            <span key={s} className="px-2 py-1 bg-indigo-900/40 rounded text-xs text-indigo-300">
              {s}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Secondary archetype block */}
      {secondary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-4 p-4 rounded-xl bg-primary-800/30 border border-primary-700/50"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{secondary.emoji}</span>
            <div>
              <h3 className="font-medium text-white">{secondary.displayName} <span className="text-primary-500 text-xs">(вторичный)</span></h3>
            </div>
          </div>
          <p className="text-sm text-primary-400 leading-relaxed">{secondary.description}</p>
        </motion.div>
      )}

      {/* Shadow risk */}
      {shadow && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-4 p-4 rounded-xl bg-amber-950/20 border border-amber-900/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">⚠️</span>
            <h3 className="text-sm font-medium text-amber-400/80">Теневой риск: {shadow.displayName}</h3>
          </div>
          <p className="text-sm text-primary-400">{shadow.riskDescription}</p>
        </motion.div>
      )}

      {/* Basic profile note */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="mb-5 p-3 rounded-xl bg-primary-800/20 border border-primary-700/30"
      >
        <p className="text-xs text-primary-400 text-center">
          🔮 Это базовый профиль. Пройди Диагностику B и C, чтобы уточнить характеристики.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="mt-auto space-y-3"
      >
        <Button onClick={onContinue} fullWidth size="lg">
          Продолжить
        </Button>
      </motion.div>
    </motion.div>
  );
}
